import { Link } from 'gatsby';
import React, { useEffect, useState } from 'react';
import { useLocation } from '@reach/router';
import { StaticImage } from 'gatsby-plugin-image';
import clsx from 'clsx';
import {
  EmailIcon,
  CallIcon,
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  CloseIcon,
} from '../../assets/Icons';
import { Button } from '../Button';
import { useModal } from '../../hooks/useModal';
import seedsLogo from '../../assets/images/seeds-logo-main.png';

const navigation = [
  { name: 'WHAT WE DO', href: '/what-we-do' },
  { name: 'WHO WE ARE', href: '/who-we-are' },
  { name: 'CONNECT WITH US', href: '/contact-us' },
];

export const Header = () => {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();
  const { openContactModal } = useModal();
  const isHomePage = location.pathname === '/' || location.pathname === '';

  // Close mobile drawer on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={clsx(
        'z-50',
        isHomePage
          ? 'relative md:absolute top-0 left-0 right-0 bg-transparent'
          : 'relative bg-seeds-light'
      )}
    >
      <div className='container mx-auto md:px-3'>
        {/* Utility Bar */}
        <div
          className={clsx(
            'mx-auto py-3 px-4 hidden md:block',
            isHomePage ? 'backdrop-blur-sm' : 'bg-seeds-light'
          )}
        >
          <div className='container mx-auto flex items-center justify-end gap-5'>
            {/* Contact Information */}
            <div className='flex items-center space-x-8'>
              <div className='flex items-center space-x-2'>
                <span className='text-dark-gray font-inter-bold text-sm'>
                  FCRA Reg No.
                </span>
                <span className='text-[#006543] font-inter-bold text-sm'>
                  231661523
                </span>
              </div>
              <div className='flex items-center space-x-2'>
                <EmailIcon
                  className='w-4 h-4 text-[#F8BD17]'
                  fill='currentColor'
                />
                <a
                  href='mailto:info@seedsimpact.org'
                  className='text-[#141F19] font-inter-bold text-sm hover:text-seeds-dark transition-colors duration-200'
                >
                  info@seedsimpact.org
                </a>
              </div>
              <div className='flex items-center space-x-2'>
                <CallIcon
                  className='w-4 h-4 text-[#F8BD17]'
                  fill='currentColor'
                />
                <a
                  href='tel:+15551234567'
                  className='text-[#141F19] font-inter-bold text-sm hover:text-seeds-dark transition-colors duration-200'
                >
                  +91-9963334436
                </a>
              </div>
            </div>

            {/* Social Media Links */}
            <div className='flex items-center space-x-3'>
              <span className='text-[#141F19] font-inter-bold text-sm'>
                Follow Us :
              </span>
              <div className='flex items-center space-x-2'>
                <a
                  href='https://www.facebook.com/SeedsImpactIndia?_rdr'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='w-8 h-8 bg-[#F8BD17] rounded-full flex items-center justify-center hover:bg-yellow-600 transition-colors duration-200'
                  aria-label='Facebook'
                >
                  <FacebookIcon className='w-4 h-4' fill='white' />
                </a>
                <a
                  href='https://instagram.com/seedsimpact'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='w-8 h-8 bg-[#F8BD17] rounded-full flex items-center justify-center hover:bg-yellow-600 transition-colors duration-200'
                  aria-label='Instagram'
                >
                  <InstagramIcon className='w-4 h-4' fill='white' />
                </a>
                <a
                  href='https://www.linkedin.com/company/social-empowerment-and-economic-development-society-seeds/'
                  target='_blank'
                  rel='noopener noreferrer'
                  className='w-8 h-8 bg-[#F8BD17] rounded-full flex items-center justify-center hover:bg-yellow-600 transition-colors duration-200'
                  aria-label='LinkedIn'
                >
                  <LinkedinIcon className='w-4 h-4' fill='white' />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main Navigation Bar (Desktop) */}
        <div className='hidden md:flex bg-seeds-dark rounded-full shadow-lg relative z-10'>
          <div className='container mx-auto pr-6'>
            <div className='flex items-center justify-between'>
              <div className='flex gap-5'>
                {/* Logo */}
                <Link to='/' className='flex items-center space-x-4'>
                  <div className='w-auto bg-white px-3 py-1 rounded-full flex items-center justify-center'>
                    <img
                      src={seedsLogo}
                      alt='Seeds Impact Logo'
                      className='w-48 h-16 object-contain'
                    />
                  </div>
                </Link>

                {/* Navigation Links */}
                <nav className='flex items-center space-x-8'>
                  {navigation.map(item => {
                    const isActive =
                      location.pathname === item.href ||
                      location.pathname === `${item.href}/`;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={`font-inter-bold text-md uppercase tracking-wide transition-colors duration-200 ${
                          isActive
                            ? 'text-seeds-gold'
                            : 'text-white hover:text-seeds-gold'
                        }`}
                      >
                        {item.name}
                      </Link>
                    );
                  })}
                </nav>
              </div>

              {/* Donate Now Button */}
              <Button
                variant='primary'
                size='sm'
                textColor='text-black'
                onClick={openContactModal}
              >
                PARTNER WITH US
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Header */}
        <div className='md:hidden'>
          <div className='flex items-center justify-between bg-seeds-dark md:rounded-2xl pr-4'>
            {/* Logo */}
            <Link to='/' className='flex items-center'>
              <img
                src={seedsLogo}
                alt='Seeds Impact Logo'
                className='h-12 w-auto object-contain bg-white px-3 py-2 rounded-tr-full rounded-br-full'
              />
            </Link>
            {/* Hamburger */}
            <button
              onClick={() => setIsMobileOpen(true)}
              className='flex items-center px-3 py-2  rounded text-[#fff]  cursor-pointer'
            >
              <svg
                className='fill-current h-6 w-6'
                viewBox='0 0 20 20'
                xmlns='http://www.w3.org/2000/svg'
              >
                <path d='M0 3h20v2H0V3zm0 6h20v2H0V9zm0 6h20v2H0v-2z'></path>
              </svg>
            </button>
          </div>

          {/* Mobile Drawer */}
          <div
            className={`fixed inset-0 z-50 ${
              isMobileOpen ? '' : 'pointer-events-none'
            }`}
          >
            {/* Backdrop */}
            <div
              className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
                isMobileOpen ? 'opacity-100' : 'opacity-0'
              }`}
              onClick={() => setIsMobileOpen(false)}
            />
            {/* Panel */}
            <aside
              className={`absolute right-0 top-0 h-full w-80 max-w-[85%] bg-white shadow-xl transition-transform duration-300 ease-out ${
                isMobileOpen ? 'translate-x-0' : 'translate-x-full'
              }`}
              role='dialog'
              aria-modal='true'
            >
              <div className='flex items-center justify-between px-5 py-4'>
                <StaticImage
                  src='../../assets/images/seeds-logo-white.png'
                  alt='Seeds Impact Logo'
                  className='h-10 w-auto object-contain'
                  placeholder='blurred'
                  layout='constrained'
                />
                <button
                  aria-label='Close menu'
                  onClick={() => setIsMobileOpen(false)}
                  className='h-9 w-9 rounded-full flex items-center justify-center text-seeds-dark hover:bg-gray-100 active:scale-95 transition'
                >
                  <span className='sr-only'>Close</span>
                  <CloseIcon className='w-6 h-6 text-black' strokeWidth={2} />
                </button>
              </div>
              <nav className='px-5 pt-2 pb-6 flex flex-col gap-6'>
                {navigation.map(item => {
                  const isActive =
                    location.pathname === item.href ||
                    location.pathname === `${item.href}/`;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`z-[101] text-md md:text-xl font-inter-semibold transition-colors ${
                        isActive
                          ? 'text-seeds-dark'
                          : 'text-gray-700 hover:text-seeds-dark'
                      }`}
                    >
                      {item.name}
                    </Link>
                  );
                })}
                <Button
                  variant='primary'
                  size='md'
                  textColor='text-black'
                  onClick={() => {
                    setIsMobileOpen(false);
                    openContactModal();
                  }}
                >
                  PARTNER WITH US
                </Button>
              </nav>
            </aside>
          </div>
        </div>
      </div>
    </header>
  );
};
